import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded'
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import ContentPasteRoundedIcon from '@mui/icons-material/ContentPasteRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import FitScreenRoundedIcon from '@mui/icons-material/FitScreenRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import InterestsRoundedIcon from '@mui/icons-material/InterestsRounded'
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded'
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded'
import MobileScreenShareRoundedIcon from '@mui/icons-material/MobileScreenShareRounded'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { useTimeline } from '../context/useTimeline'
import { useChecklistDetailPopup } from '../context/useChecklistDetailPopup'
import { DashboardViewControlsSlotProvider } from '../context/dashboardViewControlsSlot'
import { type TimelineElement } from '../context/TimelineContext'
import { useAuth } from '../context/useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import { useToast } from '../context/useToast'
import { useClientPreferences } from '../context/useClientPreferences'
import { useNavigation } from '../context/useNavigation'
import { useWorkspace } from '../context/useWorkspace'
import {
  AVAILABLE_VIEW_KEYS,
  COMING_SOON_VIEW_KEYS,
  TIMELINE_BOARD_LABELS,
  type DashboardViewKey,
  type TimelineBoardKey,
} from '../types/schema'
import { useCanvasPermissions } from '../hooks/useCanvasPermissions'
import { usePersistedState } from '../hooks/usePersistedState'
import { useOrbit } from './Common/Orbit/useOrbit'
import { buildLcmChipSlices } from './Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from './Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from './Common/Orbit/orbitSliceHelpers'
import {
  buildDashboardCanvasSlices,
  buildGoalRowSlices,
  buildMarkerSlices,
  buildTaskCardSlices,
} from './DashboardContainer.orbit'
import { resolveOrbitActionId } from './Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../services/actions/registry'
import { restructureElements } from '../services/elementRestructureService'
import type { DashboardActionOps } from '../services/actions/action'
import { useXRay, resolveAiAnchor } from './Common/XRay'
import { useLongPress } from '../hooks/useLongPress'
import { switchedOffModulesFor } from '../utils/workspaceModules'
import { useViewProjection } from '../hooks/useViewProjection'
import { computeTodayContext } from '../utils/todayContext'
import { buildRecurringOccurrenceMap } from '../utils/viewProjection'
import { ConstellationView } from './views/ConstellationView'
import { JourneyView } from './views/JourneyView'
import { RadarView } from './views/RadarView'
import { SandfallView } from './views/SandfallView'
import { TimelineView } from './views/TimelineView'
import { ContributionHeatmapView } from './views/ContributionHeatmapView'
import { StripboardMatrixView } from './views/StripboardMatrixView'
import { WarRoomBlueprintView } from './views/WarRoomBlueprintView'
import { PerimeterCountdownView } from './views/PerimeterCountdownView'
import { LootDropLanesView } from './views/LootDropLanesView'
import { LeaderboardView } from './views/LeaderboardView'
import { FlowbandView } from './views/FlowbandView'
import { BurnshoreView } from './views/BurnshoreView'
import { CadenceView } from './views/CadenceView'
import { focusWindowForElement, getLayoutTransition, getPaddedElementBounds, splitMacroElements } from './views/viewUtils'
import { AppDialog } from './Common/AppDialog'
import { useHover, HoverBubble } from './Common/Hover'
import { Button } from './Common/Button'
import { IconButton } from './Common/IconButton'
import { Breadcrumb, StatusChip, TextInput, type CrumbSpec, type DropdownItem } from './Common'
import { Loader } from './Common/Loader'
import { PannableCanvas, type PannableCanvasController } from './Canvas/PannableCanvas'
import type { PannableCanvasTransform } from './Canvas/usePannableCanvasState'
import {
  DASHBOARD_VIEW_MAP,
  type DashboardView as SharedDashboardView,
} from './Common/mobileDashboardDockConstants'
import { firestoreDb } from '../services/firebase'
import { recordWorkspaceActivity } from '../services/activityService'
import { serializeViewContext, syncToUser } from '../services/ViewContextService'
import { useResourceReadiness } from '../hooks/useResourceReadiness'
import { ResourceReadinessPanel } from './Resources/ResourceReadinessPanel'
import { shouldShowResourceReadinessPanel } from './Resources/resourceReadinessVisibility'
import type { DashboardViewProps } from '../types/dashboardView'
import { useNls } from '../nls/nlsProvider'
import { getDashboardControlLabels, getDashboardViewDockLabels } from './DashboardNls'
import { getViewDescriptor } from './Dashboard/viewDescriptors'

type DashboardView = SharedDashboardView
const dashboardViewMap = DASHBOARD_VIEW_MAP

const expandDashboardViewKeys = (keys: readonly DashboardViewKey[]): DashboardViewKey[] => {
  if (!keys.includes('HEATMAP') || keys.includes('GRID')) return [...keys]
  const expanded: DashboardViewKey[] = []
  keys.forEach((key) => {
    if (key === 'HEATMAP') expanded.push('GRID')
    expanded.push(key)
  })
  return expanded
}
const DRAG_THRESHOLD_PX = 5
const DOUBLE_TAP_MS = 320
const DOUBLE_TAP_DISTANCE_PX = 28
const MOBILE_LONG_PRESS_MS = 450

/**
 * Parses the newline-separated string emitted into `data-hover-info` by the
 * view renderers (see `getHoverTitle` in views/viewUtils.ts). Structure is:
 *   line 0: element TYPE (GOAL / TASK / SUBTASK / CHECKLIST)
 *   line 1: title
 *   line 2: description (optional, long)
 *   line 3+: meta lines (range, % in view, cadence, etc.)
 * Anything not matching that shape is rendered as a single title line.
 */
function parseDashboardHoverText(raw: string, accent?: string): import('./Common/Hover').HoverContentSchema {
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean)
  if (lines.length === 0) return { title: '', accent }
  const kickerCandidate = lines[0]
  const looksLikeType = /^[A-Z][A-Z_ ]{1,20}$/.test(kickerCandidate)
  if (!looksLikeType) return { title: lines.join(' · '), accent }
  const title = lines[1] ?? kickerCandidate
  const rest = lines.slice(2)
  let subtitle: string | undefined
  let meta: string[] = rest
  if (rest.length > 0 && rest[0].length > 30 && !/^\d/.test(rest[0])) {
    subtitle = rest[0]
    meta = rest.slice(1)
  }
  return { kicker: kickerCandidate, title, subtitle, meta: meta.length > 0 ? meta : undefined, accent }
}

type DashboardClipboard =
  | { op: 'copy' | 'cut'; element: TimelineElement }
  | null

type DashboardConfirmIntent = {
  title: string
  message: ReactNode
  confirmLabel: string
  variant?: 'primary' | 'danger'
  run: () => Promise<void>
} | null

export type { DashboardViewProps } from '../types/dashboardView'

const viewComponents: Record<DashboardView, ComponentType<DashboardViewProps>> = {
  Timeline: TimelineView,
  Constellation: ConstellationView,
  Radar: RadarView,
  Sandfall: SandfallView,
  Journey: JourneyView,
  Grid: ContributionHeatmapView.Grid,
  Heatmap: ContributionHeatmapView.Heatmap,
  Stripboard: StripboardMatrixView,
  WarRoom: WarRoomBlueprintView,
  // Remaining specialty views read live data directly from their own hooks
  // rather than the legacy DashboardViewProps prop bag. Excess props are
  // ignored at runtime; the cast is safe because TypeScript treats no-arg
  // components as assignable to the wider signature.
  Perimeter: PerimeterCountdownView as ComponentType<DashboardViewProps>,
  LootDrop: LootDropLanesView as ComponentType<DashboardViewProps>,
  Leaderboard: LeaderboardView as ComponentType<DashboardViewProps>,
  Flowband: FlowbandView,
  Burnshore: BurnshoreView,
  Cadence: CadenceView,
}

type DashboardContainerProps = {
  activeBoard?: TimelineBoardKey
  summary?: {
    goalCount: number
    totalDays: number
    rangeLabel: string
  }
  /** When set, switch to this dashboard view on mount/update and notify via
   *  `onPendingViewConsumed`. Used by the global mobile dock to request a view
   *  switch when the user is on a non-Dashboard route. */
  onToggleWatch?: () => void
  isWatching?: boolean
  isWatchAvailable?: boolean
  pendingView?: DashboardView | null
  onPendingViewConsumed?: () => void
  /** Reports the active dashboard view up to App.tsx so the global mobile
   *  dock can highlight the currently-active view. */
  onCurrentViewChange?: (view: DashboardView) => void
  activeView?: DashboardView | null
  onActiveViewChange?: (view: DashboardView) => void
}

type PointerGesture = {
  pointerId: number
  mode: 'pending' | 'pan' | 'zoom'
  startX: number
  startY: number
  lastX: number
  lastY: number
  anchorX: number
  anchorY: number
  lastDistance: number
  moved: boolean
  hasCapture: boolean
}

export const createCompleteTaskHandler = (
  elementById: Map<string, TimelineElement>,
  updateElement: (id: string, patch: Partial<TimelineElement> & Record<string, unknown>) => Promise<unknown> | unknown,
) => async (id: string) => {
  const task = elementById.get(id)
  if (!task) throw new Error(`Task ${id} not found`)
  const isCompleted = task.boardStatus === 'COMPLETE' || (task.progress ?? 0) >= 100
  await updateElement(id, {
    boardStatus: isCompleted ? 'TODO' : 'COMPLETE',
    ...(isCompleted ? { progress: 0 } : {}),
  })
}

export const DashboardContainer = ({
  activeBoard = 'DASHBOARD',
  summary: _summary,
  pendingView = null,
  onPendingViewConsumed,
  onCurrentViewChange,
  activeView = null,
  onActiveViewChange,
  onToggleWatch,
  isWatching = false,
  isWatchAvailable = false,
}: DashboardContainerProps) => {
  const { locale, t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const { workspaces, activeWorkspaceId, activeRole, activeMemberDefaultView, canPerform } = useWorkspace()
  const { showToast } = useToast()
  const { open: openXRay } = useXRay()
  const lcmDispatchers = useLcmChipDispatchers()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const [viewWindow, setViewWindow] = usePersistedState<{ startMs: number; endMs: number } | null>('dashboard.viewWindow', null)
  const dashboardHover = useHover({ variant: 'rich', placement: 'top', delay: { open: 220, close: 0 }, group: 'dashboardItems' })
  const { show: showDashboardHoverRaw, hide: hideDashboardHoverRaw } = dashboardHover
  const dashboardHoverAnchorRef = useRef<HTMLElement | null>(null)
  const collectDashboardAvoidZones = useCallback(() => {
    if (typeof document === 'undefined') return []
    const anchor = dashboardHoverAnchorRef.current
    const nodes = document.querySelectorAll<HTMLElement>('[data-hover-info]')
    const rects: DOMRect[] = []
    nodes.forEach((node) => {
      if (anchor && node === anchor) return
      const rect = node.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) return
      rects.push(rect)
    })
    return rects
  }, [])
  const showDashboardHover = useCallback((target: HTMLElement) => {
    const raw = target.dataset.hoverInfo
    if (!raw) return
    dashboardHoverAnchorRef.current = target
    showDashboardHoverRaw({ anchor: target, content: parseDashboardHoverText(raw, target.dataset.hoverAccent) })
  }, [showDashboardHoverRaw])
  const hideDashboardHover = useCallback(() => {
    dashboardHoverAnchorRef.current = null
    hideDashboardHoverRaw()
  }, [hideDashboardHoverRaw])
  const [dashboardClipboard, setDashboardClipboard] = useState<DashboardClipboard>(null)
  const [confirmIntent, setConfirmIntent] = useState<DashboardConfirmIntent>(null)
  const [isDetailActionRunning, setIsDetailActionRunning] = useState(false)
  const [isStageExpanded, setIsStageExpanded] = useState(false)
  /* Slot for view-specific controls in the workbench bar. Views (currently
     only Radar) can portal chips/toggles into this DOM element via
     `useDashboardViewControlsSlot`; other views leave it empty. */
  const [viewControlsSlotEl, setViewControlsSlotEl] = useState<HTMLElement | null>(null)
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const pointerGestureRef = useRef<PointerGesture | null>(null)
  const mobileLongPressTargetRef = useRef<{
    element: HTMLElement
    x: number
    y: number
  } | null>(null)
  const mobileLongPressTriggeredRef = useRef(false)
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null)
  const suppressClickRef = useRef(false)
  const viewWindowRef = useRef<{ startMs: number; endMs: number } | null>(null)
  const scheduledWindowRef = useRef<{ startMs: number; endMs: number } | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const {
    elements,
    checklistItems,
    isLoading,
    error,
    selectedElementId,
    setSelectedElementId,
    createElements,
    createElement,
    updateElement,
    deleteElement,
    verifyUserOperationClearance,
    isPublicReadOnly,
    canPerformOnNode,
    pathOnlyElementIds = new Set(),
    isAccessRestricted = false,
  } = useTimeline()
  const { preferences, isMobileViewport } = useClientPreferences()
  const { canReadElement } = useCanvasPermissions()
  const {
    currentLevel,
    activeGoalId,
    activeTaskId,
    activeSubTaskId,
    goToDashboard,
    goToGoal,
    goToTask,
    goToSubTask,
    jumpToLevel,
  } = useNavigation()
  const { openChecklistDetail } = useChecklistDetailPopup()
  const viewDockLabels = useMemo(() => getDashboardViewDockLabels(t), [t])
  const dashboardControlLabels = useMemo(() => getDashboardControlLabels(t), [t])

  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId],
  )
  const resourceReadiness = useResourceReadiness(activeWorkspace, checklistItems, elements)
  const showResourceReadinessPanel = shouldShowResourceReadinessPanel({
    currentLevel,
  })

  // Default = AVAILABLE_VIEW_KEYS. If a workspace had
  // a coming-soon view persisted from earlier seeding, filter it out here so
  // it never renders even before its settings checkbox is touched.
  const funPackOff = switchedOffModulesFor(activeWorkspace).has('funPack')
  const enabledViewKeys: DashboardViewKey[] = expandDashboardViewKeys(
    activeWorkspace?.enabledDashboardViews?.length ? activeWorkspace.enabledDashboardViews : AVAILABLE_VIEW_KEYS
  ).filter((key) => !COMING_SOON_VIEW_KEYS.has(key) && !(funPackOff && key === 'LEADERBOARD'))
  const enabledSignature = enabledViewKeys.join(',')
  const isDashboardViewKey = (key: string): key is DashboardViewKey => key in dashboardViewMap
  const enabledViews = useMemo<DashboardView[]>(
    () => enabledSignature
      .split(',')
      .filter(isDashboardViewKey)
      .map((key) => dashboardViewMap[key]),
    [enabledSignature],
  )
  const resolvedDefaultView = useMemo<DashboardView>(() => {
    const enabledKeys = enabledSignature.split(',').filter(isDashboardViewKey)
    const inEnabled = (key: DashboardViewKey | null | undefined) =>
      key != null && isDashboardViewKey(key) && enabledKeys.includes(key) ? dashboardViewMap[key] : null
    return (
      inEnabled(activeMemberDefaultView) ??
      inEnabled(activeWorkspace?.defaultDashboardView) ??
      enabledViews[0] ??
      'Timeline'
    )
  }, [activeMemberDefaultView, activeWorkspace?.defaultDashboardView, enabledSignature, enabledViews])

  const [currentView, setCurrentView] = usePersistedState<DashboardView>('dashboard.currentView', resolvedDefaultView)
  const [appliedWorkspaceId, setAppliedWorkspaceId] = usePersistedState<string | null>('dashboard.appliedWorkspaceId', null)
  const setDashboardView = useCallback((view: DashboardView) => {
    setCurrentView(view)
    onActiveViewChange?.(view)
  }, [onActiveViewChange, setCurrentView])

  // Open on the workspace's resolved default when its data first loads or when switching
  // workspaces. Adjusting state during render is the recommended alternative to an effect here.
  if (activeWorkspace && appliedWorkspaceId !== activeWorkspaceId) {
    setAppliedWorkspaceId(activeWorkspaceId)
    setDashboardView(activeView && enabledViews.includes(activeView) ? activeView : resolvedDefaultView)
  }

  // Honor a pending view requested from outside (e.g., the global mobile dock
  // switching us back to Dashboard with a specific view selected).
  useEffect(() => {
    if (!pendingView) return
    if (enabledViews.includes(pendingView)) {
      setDashboardView(pendingView)
    }
    onPendingViewConsumed?.()
  }, [pendingView, enabledViews, onPendingViewConsumed, setDashboardView])

  // If the enabled set no longer includes the active view, fall back to the resolved default.
  const requestedCurrentView = activeView && enabledViews.includes(activeView) ? activeView : currentView
  const safeCurrentView: DashboardView = enabledViews.includes(requestedCurrentView) ? requestedCurrentView : resolvedDefaultView

  // Notify App.tsx of the active view so the global mobile dock highlights it.
  useEffect(() => {
    onCurrentViewChange?.(safeCurrentView)
  }, [safeCurrentView, onCurrentViewChange])
  const [ghostEchoPromptOpen, setGhostEchoPromptOpen] = useState(false)
  const [ghostEchoTargetUid, setGhostEchoTargetUid] = useState('')
  const [ghostEchoPayload, setGhostEchoPayload] = useState<{ activeElement: TimelineElement | null; checklistOpen: boolean } | null>(null)
  const [isSendingGhostEcho, setIsSendingGhostEcho] = useState(false)

  const sendGhostEcho = (activeElement: TimelineElement | null = null, checklistOpen = false) => {
    if (!currentUser || !activeWorkspace) return
    setGhostEchoTargetUid(currentUser.uid)
    setGhostEchoPayload({ activeElement, checklistOpen })
    setGhostEchoPromptOpen(true)
  }

  const handleConfirmGhostEcho = async () => {
    if (!currentUser || !activeWorkspace) return
    const targetUid = ghostEchoTargetUid.trim()
    if (!targetUid) {
      setGhostEchoPromptOpen(false)
      setGhostEchoPayload(null)
      return
    }
    const { activeElement, checklistOpen } = ghostEchoPayload ?? { activeElement: null, checklistOpen: false }
    const currentViewEntry = Object.entries(dashboardViewMap).find(([, v]) => v === safeCurrentView)
    const currentViewKey = currentViewEntry?.[0]
    const dashboardView = currentViewKey && isDashboardViewKey(currentViewKey) ? currentViewKey : null
    setIsSendingGhostEcho(true)
    try {
      await syncToUser(targetUid, serializeViewContext({
        senderUid: currentUser.uid,
        senderName: currentDisplayName || currentUser.email || t('NLSDashboardSenderDesktopFallback'),
        workspace: activeWorkspace,
        board: activeBoard,
        dashboardView,
        activeElement,
        checklistOpen,
        filters: {
          level: currentLevel,
          selectedElementId,
        },
      }))
      showToast(t('NLSDashboardToastGhostEchoSent'), 'ghost')
      setGhostEchoPromptOpen(false)
      setGhostEchoPayload(null)
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSDashboardToastGhostEchoFailed'), 'error')
    } finally {
      setIsSendingGhostEcho(false)
    }
  }

  const viewControls = getViewDescriptor(safeCurrentView)
  const canvasControllerRef = useRef<PannableCanvasController | null>(null)
  const [canvasTransform, setCanvasTransform] = useState<PannableCanvasTransform>({ x: 0, y: 0, zoom: 1 })

  const handleCanvasControllerReady = useCallback((controller: PannableCanvasController | null) => {
    canvasControllerRef.current = controller
  }, [])

  const handleCanvasTransformChange = useCallback((transform: PannableCanvasTransform) => {
    setCanvasTransform(transform)
  }, [])

  const viewProjection = useViewProjection(safeCurrentView)
  const ActiveView = viewComponents[safeCurrentView]
  const viewTransition = getLayoutTransition(preferences.animationProfile)
  const boardElements = useMemo(
    () => elements.filter((element) => (element.board ?? 'DASHBOARD') === 'DASHBOARD'),
    [elements],
  )
  const elementById = useMemo(() => new Map(boardElements.map((element) => [element.id, element])), [boardElements])

  const visibleElements = useMemo(() => {
    const readableElements = boardElements.filter(canReadElement)

    if (currentLevel === 'dashboard') return readableElements

    const activeGoal = activeGoalId ? elementById.get(activeGoalId) : null
    if (!activeGoal || !canReadElement(activeGoal)) return readableElements

    if (currentLevel === 'goal') {
      const tasks = readableElements.filter((element) => element.parentId === activeGoal.id)
      const taskIds = new Set(tasks.map((task) => task.id))
      const subTasks = readableElements.filter((element) => element.parentId && taskIds.has(element.parentId))
      return [activeGoal, ...tasks, ...subTasks]
    }

    const activeTask = activeTaskId ? elementById.get(activeTaskId) : null
    if (currentLevel === 'task' && activeTask && canReadElement(activeTask)) {
      return [
        activeGoal,
        activeTask,
        ...readableElements.filter((element) => element.parentId === activeTask.id),
      ]
    }

    const activeSubTask = activeSubTaskId ? elementById.get(activeSubTaskId) : null
    if (currentLevel === 'subtask' && activeTask && activeSubTask && canReadElement(activeTask) && canReadElement(activeSubTask)) {
      return [activeGoal, activeTask, activeSubTask]
    }

    return [activeGoal]
  }, [activeGoalId, activeSubTaskId, activeTaskId, canReadElement, currentLevel, elementById, boardElements])

  const visibleBounds = useMemo(() => getPaddedElementBounds(visibleElements), [visibleElements])
  const activeViewElements = visibleElements

  useEffect(() => {
    if (!visibleBounds) return
    setViewWindow((current) => current ?? visibleBounds)
  }, [visibleBounds])

  const effectiveWindow = viewWindow ?? visibleBounds

  useEffect(() => {
    viewWindowRef.current = effectiveWindow
  }, [effectiveWindow])

  useEffect(() => () => {
    if (animationFrameRef.current !== null) window.cancelAnimationFrame(animationFrameRef.current)
  }, [])

  useEffect(() => {
    if (!isStageExpanded) return
    if (typeof document === 'undefined') return
    document.documentElement.classList.add('dashboardStageFullscreen')
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsStageExpanded(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.documentElement.classList.remove('dashboardStageFullscreen')
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isStageExpanded])

  const clampWindow = (startMs: number, endMs: number) => {
    if (!visibleBounds) return { startMs, endMs }

    const minDuration = 7 * 24 * 60 * 60 * 1000
    const maxDuration = Math.max(minDuration, visibleBounds.endMs - visibleBounds.startMs)
    const duration = Math.min(maxDuration, Math.max(minDuration, endMs - startMs))
    let nextStart = startMs
    let nextEnd = startMs + duration

    if (nextStart < visibleBounds.startMs) {
      nextStart = visibleBounds.startMs
      nextEnd = nextStart + duration
    }

    if (nextEnd > visibleBounds.endMs) {
      nextEnd = visibleBounds.endMs
      nextStart = nextEnd - duration
    }

    return { startMs: nextStart, endMs: nextEnd }
  }

  const focusElementWindow = (element: TimelineElement | null | undefined) => {
    if (!element?.startDate || !element.endDate) return
    const nextWindow = focusWindowForElement(element)
    setViewWindow(clampWindow(nextWindow.startMs, nextWindow.endMs))
  }

  const focusDashboardWindow = useCallback(() => {
    if (viewControls.zoomMode === 'geometric') {
      canvasControllerRef.current?.fitToContent()
      return
    }
    if (visibleBounds) setViewWindow(visibleBounds)
  }, [viewControls.zoomMode, visibleBounds])

  const focusGoalWindow = (goalId: string) => {
    focusElementWindow(elementById.get(goalId))
  }

  const focusTaskWindow = (taskId: string) => {
    focusElementWindow(elementById.get(taskId))
  }

  const focusSubTaskWindow = (subTaskId: string) => {
    focusElementWindow(elementById.get(subTaskId))
  }

  useEffect(() => {
    if (!visibleBounds) return

    if (currentLevel === 'dashboard') {
      setViewWindow(visibleBounds)
      return
    }

    const activeElement =
      currentLevel === 'goal'
        ? activeGoalId ? elementById.get(activeGoalId) : null
        : currentLevel === 'task'
          ? activeTaskId ? elementById.get(activeTaskId) : null
          : activeSubTaskId ? elementById.get(activeSubTaskId) : null

    if (!activeElement?.startDate || !activeElement.endDate) return
    const nextWindow = focusWindowForElement(activeElement)
    setViewWindow(clampWindow(nextWindow.startMs, nextWindow.endMs))
  }, [activeGoalId, activeSubTaskId, activeTaskId, currentLevel, elementById, visibleBounds])

  const setViewWindowSmooth = (nextWindow: { startMs: number; endMs: number }) => {
    viewWindowRef.current = nextWindow
    scheduledWindowRef.current = nextWindow

    if (animationFrameRef.current !== null) return

    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null
      if (!scheduledWindowRef.current) return
      setViewWindow(scheduledWindowRef.current)
      scheduledWindowRef.current = null
    })
  }

  const zoomAt = (anchorRatio: number, zoomFactor: number) => {
    const currentWindow = viewWindowRef.current
    if (!currentWindow) return

    const duration = currentWindow.endMs - currentWindow.startMs
    const anchorMs = currentWindow.startMs + duration * anchorRatio
    const nextDuration = duration * zoomFactor
    const nextStart = anchorMs - nextDuration * anchorRatio
    const nextEnd = nextStart + nextDuration

    setViewWindowSmooth(clampWindow(nextStart, nextEnd))
  }

  const handleDashboardZoomIn = useCallback(() => {
    if (viewControls.zoomMode === 'density' || viewControls.zoomMode === 'none') return
    if (viewControls.zoomMode === 'geometric') {
      canvasControllerRef.current?.zoomIn()
      return
    }
    zoomAt(0.5, 0.72)
  }, [viewControls.zoomMode])

  const handleDashboardZoomOut = useCallback(() => {
    if (viewControls.zoomMode === 'density' || viewControls.zoomMode === 'none') return
    if (viewControls.zoomMode === 'geometric') {
      canvasControllerRef.current?.zoomOut()
      return
    }
    zoomAt(0.5, 1.35)
  }, [viewControls.zoomMode])

  const panByPixels = (pixelDelta: number) => {
    const currentWindow = viewWindowRef.current
    if (!currentWindow) return

    const bounds = viewportRef.current?.getBoundingClientRect()
    const width = Math.max(1, bounds?.width ?? window.innerWidth)
    const duration = currentWindow.endMs - currentWindow.startMs
    const msDelta = (pixelDelta / width) * duration

    setViewWindowSmooth(clampWindow(currentWindow.startMs + msDelta, currentWindow.endMs + msDelta))
  }

  const scrollStageHorizontally = (direction: -1 | 1, amount: 'small' | 'large') => {
    const stage = viewportRef.current?.querySelector<HTMLElement>('.dashboardViewStage')
    const scrollTarget =
      stage?.querySelector<HTMLElement>('[data-dashboard-horizontal-scroll="true"]') ??
      stage?.querySelector<HTMLElement>('.stripboardViewport')

    const baseWidth = Math.max(1, scrollTarget?.clientWidth ?? stage?.clientWidth ?? window.innerWidth)
    const delta = direction * baseWidth * (amount === 'large' ? 0.86 : 0.28)

    if (scrollTarget) {
      scrollTarget.scrollBy({ left: delta, behavior: 'smooth' })
      return
    }

    if (stage) {
      stage.scrollBy({ left: delta, behavior: 'smooth' })
    }
  }

  const shiftViewSideways = (direction: -1 | 1, amount: 'small' | 'large') => {
    const width = Math.max(1, viewportRef.current?.getBoundingClientRect().width ?? window.innerWidth)
    if (viewControls.datePan) {
      panByPixels(direction * width * (amount === 'large' ? 0.74 : 0.26))
      return
    }
    if (viewControls.horizontalScroll) {
      scrollStageHorizontally(direction, amount)
    }
  }

  const zoomByDragDistance = (anchorX: number, anchorY: number, previousDistance: number, nextX: number, nextY: number) => {
    const nextDistance = Math.max(1, Math.hypot(nextX - anchorX, nextY - anchorY))
    const zoomFactor = Math.max(0.72, Math.min(1.28, previousDistance / nextDistance))

    zoomAt(getAnchorRatio(anchorX), zoomFactor)
    return nextDistance
  }

  const getAnchorRatio = (clientX: number) => {
    const bounds = viewportRef.current?.getBoundingClientRect()
    if (!bounds || bounds.width === 0) return 0.5
    return Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width))
  }

  const triggerMobileContextTooltip = (target: HTMLElement, _x: number, _y: number) => {
    if (!target.dataset.hoverInfo) return
    mobileLongPressTriggeredRef.current = true
    showDashboardHover(target)
  }

  const mobileLongPress = useLongPress({
    ms: MOBILE_LONG_PRESS_MS,
    disabled: !isMobileViewport,
    onLongPress: () => {
      const target = mobileLongPressTargetRef.current
      if (target) triggerMobileContextTooltip(target.element, target.x, target.y)
    },
  })

  const clearMobileLongPress = () => {
    mobileLongPress.onPointerCancel()
    mobileLongPressTargetRef.current = null
  }

  const handleElementClick = (element: TimelineElement) => {
    if (pathOnlyElementIds.has(element.id)) return
    if (!canReadElement(element)) return

    setSelectedElementId(element.id)
    setViewWindow(clampWindow(focusWindowForElement(element).startMs, focusWindowForElement(element).endMs))

    if (element.type === 'GOAL') {
      goToGoal(element.id)
      return
    }

    if (element.type === 'TASK' && element.parentId) {
      goToTask(element.parentId, element.id)
      return
    }

    if (element.type === 'SUBTASK' && element.parentId) {
      const task = elementById.get(element.parentId)
      if (task?.parentId) goToSubTask(task.parentId, task.id, element.id)
    }
  }

  const openDashboardLevel = () => {
    focusDashboardWindow()
    goToDashboard()
  }

  const goalOptions = useMemo(
    () => boardElements.filter((element) => element.type === 'GOAL' && !element.parentId).sort((a, b) => a.title.localeCompare(b.title)),
    [boardElements],
  )
  const taskOptions = useMemo(
    () => activeGoalId
      ? boardElements.filter((element) => element.type === 'TASK' && element.parentId === activeGoalId).sort((a, b) => a.title.localeCompare(b.title))
      : [],
    [activeGoalId, boardElements],
  )
  const subtaskOptions = useMemo(
    () => activeTaskId
      ? boardElements.filter((element) => element.type === 'SUBTASK' && element.parentId === activeTaskId).sort((a, b) => a.title.localeCompare(b.title))
      : [],
    [activeTaskId, boardElements],
  )

  const childrenByParentId = useMemo(() => {
    const map = new Map<string, TimelineElement[]>()
    for (const element of boardElements) {
      if (!element.parentId) continue
      const children = map.get(element.parentId) ?? []
      children.push(element)
      map.set(element.parentId, children)
    }
    return map
  }, [boardElements])

  const collectSubtree = (root: TimelineElement) => {
    const descendants: TimelineElement[] = []
    const walk = (parentId: string) => {
      for (const child of childrenByParentId.get(parentId) ?? []) {
        descendants.push(child)
        walk(child.id)
      }
    }
    walk(root.id)
    return [root, ...descendants]
  }

  const dashboardDetailElement =
    currentLevel === 'goal'
      ? activeGoalId ? elementById.get(activeGoalId) ?? null : null
      : currentLevel === 'task'
        ? activeTaskId ? elementById.get(activeTaskId) ?? null : null
        : currentLevel === 'subtask'
          ? activeSubTaskId ? elementById.get(activeSubTaskId) ?? null : null
          : null
  const canRunDetailActions =
    Boolean(dashboardDetailElement) &&
    !isPublicReadOnly &&
    activeRole !== 'VIEWER' &&
    verifyUserOperationClearance('WRITE')
  const clipboardAcceptsTarget = (clipboard: DashboardClipboard, target: TimelineElement | null) => {
    if (!clipboard || !target) return false
    if (clipboard.element.type === 'TASK') return target.type === 'GOAL'
    if (clipboard.element.type === 'SUBTASK') return target.type === 'TASK'
    return false
  }
  const canPasteIntoDetail = clipboardAcceptsTarget(dashboardClipboard, dashboardDetailElement)


  const moveGoalSubtreeToBoard = async (goal: TimelineElement, targetBoard: TimelineBoardKey) => {
    if (goal.type !== 'GOAL' || !currentUser || !activeWorkspaceId) return
    await restructureElements({
      workspaceId: activeWorkspaceId,
      op: targetBoard === 'BIN' ? 'bin' : 'move',
      rootIds: [goal.id],
      targetBoard,
    })
    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: goal.workspaceId,
      actorId: currentUser.uid,
      actorName: currentDisplayName || currentUser.email,
      targetType: goal.type,
      targetId: goal.id,
      targetTitle: goal.title,
      action: targetBoard === 'BIN' ? 'DELETE' : 'MOVE_BOARD',
      summary: `Moved ${goal.title} and its internal items to ${TIMELINE_BOARD_LABELS[targetBoard]}`,
      board: targetBoard,
    }).catch((error) => console.warn('[DashboardContainer] activity write failed', error))
    setSelectedElementId(null)
    openDashboardLevel()
  }

  const cloneElementForPaste = (element: TimelineElement, root: TimelineElement, target: TimelineElement) => ({
    id: element.id,
    type: element.type,
    title: element.id === root.id ? `${element.title} copy` : element.title,
    description: element.description ?? '',
    weight: element.weight ?? 1,
    progress: element.progress ?? 0,
    parentId: element.id === root.id ? target.id : element.parentId,
    workspaceId: element.workspaceId,
    board: 'DASHBOARD' as TimelineBoardKey,
    checklist: element.checklist ?? [],
    startDate: element.startDate,
    endDate: element.endDate,
    color: element.color,
  })

  const pasteClipboardInto = async (target: TimelineElement) => {
    if (!dashboardClipboard || !clipboardAcceptsTarget(dashboardClipboard, target) || !currentUser || !activeWorkspaceId) return
    const source = dashboardClipboard.element
    const subtree = collectSubtree(source)

    if (dashboardClipboard.op === 'copy') {
      await createElements(subtree.map((element) => cloneElementForPaste(element, source, target)))
      return
    }

    await restructureElements({
      workspaceId: activeWorkspaceId,
      op: 'reparent',
      rootIds: [source.id],
      targetParentId: target.id,
      targetBoard: 'DASHBOARD',
    })
    setDashboardClipboard(null)
  }

  const requestConfirm = (intent: Exclude<DashboardConfirmIntent, null>) => {
    setConfirmIntent(intent)
  }

  const runConfirmedAction = async () => {
    if (!confirmIntent || isDetailActionRunning) return
    setIsDetailActionRunning(true)
    try {
      await confirmIntent.run()
      setConfirmIntent(null)
    } finally {
      setIsDetailActionRunning(false)
    }
  }

  const openGoalLevel = (goal: TimelineElement) => {
    focusGoalWindow(goal.id)
    jumpToLevel('goal', { goalId: goal.id })
  }

  const openTaskLevel = (goal: TimelineElement, task: TimelineElement) => {
    focusTaskWindow(task.id)
    jumpToLevel('task', { goalId: goal.id, taskId: task.id })
  }

  const openSubTaskLevel = (goal: TimelineElement, task: TimelineElement, subTask: TimelineElement) => {
    focusSubTaskWindow(subTask.id)
    jumpToLevel('subtask', {
      goalId: goal.id,
      taskId: task.id,
      subTaskId: subTask.id,
    })
  }

  const dashboardOps: DashboardActionOps = useMemo(() => ({
    createGoal: async () => {
      if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
      if (!canPerformOnNode('timeline.create', null)) throw new Error('Your workspace role cannot create timeline items.')
      const now = new Date()
      const endDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
      const newGoal = await createElement({
        title: 'New Goal',
        type: 'GOAL',
        startDate: now.toISOString(),
        endDate: endDate.toISOString(),
        parentId: null,
        board: 'DASHBOARD',
        weight: 1,
        progress: 0,
      })
      setSelectedElementId(newGoal.id)
      openGoalLevel(newGoal)
    },
    createTask: async () => {
      if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
      const parentId = activeGoalId ?? elements.find((e) => e.type === 'GOAL' && (e.board === 'DASHBOARD' || !e.board))?.id ?? null
      if (!canPerformOnNode('timeline.create', parentId)) throw new Error('Your workspace role cannot create timeline items.')
      const now = new Date()
      const endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
      const newTask = await createElement({
        title: 'New Task',
        type: 'TASK',
        startDate: now.toISOString(),
        endDate: endDate.toISOString(),
        parentId,
        board: 'DASHBOARD',
        weight: 1,
        progress: 0,
      })
      setSelectedElementId(newTask.id)
    },
    realignCanvas: () => {
      focusDashboardWindow()
    },
    showAllConnections: () => {
      showToast('All connections visible')
    },
    renameGoal: async (id: string, title?: string) => {
      const goal = elementById.get(id)
      if (!goal) throw new Error(`Goal ${id} not found`)
      if (title && title.trim()) {
        await updateElement(id, { title: title.trim() })
      } else {
        setSelectedElementId(id)
      }
    },
    reprioritizeGoal: async (id: string) => {
      const goal = elementById.get(id)
      if (!goal) throw new Error(`Goal ${id} not found`)
      setSelectedElementId(id)
    },
    linkGoal: async (id: string) => {
      const goal = elementById.get(id)
      if (!goal) throw new Error(`Goal ${id} not found`)
      setSelectedElementId(id)
    },
    deleteGoal: async (id: string) => {
      const goal = elementById.get(id)
      if (!goal) throw new Error(`Goal ${id} not found`)
      requestConfirm({
        title: t('NLSDashboardDialogMoveBinTitle', { title: goal.title }),
        message: (
          <p>
            {t('NLSDashboardDialogMoveBinMessage')}
          </p>
        ),
        confirmLabel: t('NLSDashboardActionMoveToBin'),
        variant: 'danger',
        run: () => moveGoalSubtreeToBoard(goal, 'BIN'),
      })
    },
    showGoalConnections: (id: string) => {
      const goal = elementById.get(id)
      if (!goal) throw new Error(`Goal ${id} not found`)
      setSelectedElementId(id)
    },
    completeTask: createCompleteTaskHandler(elementById, updateElement),
    duplicateTask: async (id: string) => {
      const task = elementById.get(id)
      if (!task) throw new Error(`Task ${id} not found`)
      const { id: _ignored, ...rest } = task
      await createElement({
        ...rest,
        title: `${task.title} (Copy)`,
      })
    },
    moveTask: async (id: string) => {
      const task = elementById.get(id)
      if (!task) throw new Error(`Task ${id} not found`)
      setSelectedElementId(id)
    },
    linkTask: async (id: string) => {
      const task = elementById.get(id)
      if (!task) throw new Error(`Task ${id} not found`)
      setSelectedElementId(id)
    },
    deleteTask: async (id: string) => {
      const task = elementById.get(id)
      if (!task) throw new Error(`Task ${id} not found`)
      requestConfirm({
        title: `Delete ${task.title}`,
        message: <p>Are you sure you want to delete <strong>{task.title}</strong>?</p>,
        confirmLabel: 'Delete',
        variant: 'danger',
        run: () => deleteElement(id),
      })
    },
    editMarker: (id: string) => {
      const marker = elementById.get(id)
      if (!marker) throw new Error(`Marker ${id} not found`)
      setSelectedElementId(id)
    },
    shiftMarker: (id: string) => {
      const marker = elementById.get(id)
      if (!marker) throw new Error(`Marker ${id} not found`)
      setSelectedElementId(id)
    },
    showMarkerDependencies: (id: string) => {
      const marker = elementById.get(id)
      if (!marker) throw new Error(`Marker ${id} not found`)
      setSelectedElementId(id)
    },
    deleteMarker: async (id: string) => {
      const marker = elementById.get(id)
      if (!marker) throw new Error(`Marker ${id} not found`)
      await deleteElement(id)
    },
  }), [
    activeGoalId,
    canPerform,
    createElement,
    deleteElement,
    elementById,
    elements,
    focusDashboardWindow,
    isPublicReadOnly,
    moveGoalSubtreeToBoard,
    openGoalLevel,
    requestConfirm,
    setSelectedElementId,
    showToast,
    t,
    updateElement,
  ])

  const dashboardOpsRef = useRef<DashboardActionOps | null>(null)
  dashboardOpsRef.current = dashboardOps

  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) {
      const anchor = resolveAiAnchor({
        nodeId: id,
        sliceId,
      })
      if (anchor) {
        if (id) {
          const el = document.querySelector<HTMLElement>(`[data-goal-id="${id}"], [data-task-id="${id}"], [data-marker-id="${id}"]`)
          if (el) {
            const rect = el.getBoundingClientRect()
            anchor.screenPosition = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
          }
        }
        openXRay(anchor, { sliceId, page: 'dashboard' })
        return
      }
      showToast('Coming in Orbit Phase 6')
      return
    }
    const actionId = resolveOrbitActionId(sliceId, 'dashboard')
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: currentUser?.uid ?? '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        dashboardOps: dashboardOpsRef.current ?? undefined,
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) {
      lcmDispatch(id, title)
      return
    }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
  }, [activeWorkspaceId, canPerform, currentUser?.uid, lcmDispatchers, openXRay, showToast])

  const orbitDescriptor = useMemo(() => ({
    page: 'dashboard',
    centerLabel: 'Dashboard',
    onCreate: () => dashboardOpsRef.current?.createGoal?.(),
    contextSlices: () => buildDashboardCanvasSlices(onOrbitAction),
    targets: {
      'goal-row': (node: HTMLElement) => buildGoalRowSlices(node, onOrbitAction),
      'task-card': (node: HTMLElement) => buildTaskCardSlices(node, onOrbitAction),
      marker: (node: HTMLElement) => buildMarkerSlices(node, onOrbitAction),
      canvas: () => buildDashboardCanvasSlices(onOrbitAction),
      'lcm-chip': (node: HTMLElement) => buildLcmChipSlices(node, onOrbitAction),
    },
  }), [onOrbitAction])
  useOrbit(orbitDescriptor)

  useEffect(() => {
    const node = viewportRef.current
    if (!node) return

    const handleNativeWheel = (event: WheelEvent) => {
      if (event.defaultPrevented) return
      if (!effectiveWindow) return

      const isZoomGesture = event.ctrlKey || event.metaKey
      const isHorizontalPan = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      const target = event.target instanceof Element ? event.target : null
      const nativeHorizontalScrollTarget = target?.closest('[data-dashboard-horizontal-scroll="true"]')

      if (!isZoomGesture && !isHorizontalPan) return
      if (!isZoomGesture && isHorizontalPan && nativeHorizontalScrollTarget) return

      event.preventDefault()
      event.stopPropagation()

      if (isZoomGesture) {
        const zoomFactor = Math.exp(event.deltaY * 0.0025)
        zoomAt(getAnchorRatio(event.clientX), zoomFactor)
        return
      }

      panByPixels(event.deltaX)
    }

    node.addEventListener('wheel', handleNativeWheel, { passive: false })
    return () => node.removeEventListener('wheel', handleNativeWheel)
  }, [effectiveWindow, visibleBounds])

  const isPrimaryDashboardPointer = (event: PointerEvent<HTMLElement>) =>
    event.isPrimary && (event.pointerType === 'mouse' ? event.button === 0 : true)

  const handlePointerDownCapture = (event: PointerEvent<HTMLElement>) => {
    if (!isPrimaryDashboardPointer(event)) return
    const target = event.target instanceof Element ? event.target : null
    const hoverTarget = target?.closest<HTMLElement>('[data-hover-info]')
    if (target?.closest('button, select, input, textarea, a')) return
    if (!hoverTarget && target?.closest('[data-no-dashboard-drag="true"]')) return

    if (isMobileViewport) {
      if (hoverTarget?.dataset.hoverInfo) {
        clearMobileLongPress()
        mobileLongPressTargetRef.current = {
          element: hoverTarget,
          x: event.clientX,
          y: event.clientY,
        }
        mobileLongPressTriggeredRef.current = false
        mobileLongPress.onPointerDown(event)
      }
    }

    if (target?.closest('[data-pannable-canvas="true"]')) return

    const now = window.performance.now()
    const lastTap = lastTapRef.current
    const distanceFromLastTap = lastTap
      ? Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y)
      : Number.POSITIVE_INFINITY
    const isDoubleTapDrag =
      Boolean(lastTap) &&
      now - (lastTap?.time ?? 0) <= DOUBLE_TAP_MS &&
      distanceFromLastTap <= DOUBLE_TAP_DISTANCE_PX

    pointerGestureRef.current = {
      pointerId: event.pointerId,
      mode: isDoubleTapDrag ? 'zoom' : 'pending',
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      anchorX: isDoubleTapDrag ? lastTap?.x ?? event.clientX : event.clientX,
      anchorY: isDoubleTapDrag ? lastTap?.y ?? event.clientY : event.clientY,
      lastDistance: 1,
      moved: false,
      hasCapture: false,
    }
  }

  const handlePointerMoveCapture = (event: PointerEvent<HTMLElement>) => {
    const gesture = pointerGestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return

    mobileLongPress.onPointerMove(event)

    const totalDelta = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY)

    if (!gesture.moved && totalDelta < DRAG_THRESHOLD_PX) return

    clearMobileLongPress()
    event.preventDefault()
    event.stopPropagation()
    gesture.moved = true
    suppressClickRef.current = true

    if (!gesture.hasCapture) {
      event.currentTarget.setPointerCapture?.(event.pointerId)
      gesture.hasCapture = true
    }

    if (gesture.mode === 'pending') {
      gesture.mode = 'pan'
      gesture.lastX = event.clientX
      gesture.lastY = event.clientY
      return
    }

    if (gesture.mode === 'zoom') {
      gesture.lastDistance = zoomByDragDistance(
        gesture.anchorX,
        gesture.anchorY,
        gesture.lastDistance,
        event.clientX,
        event.clientY,
      )
      return
    }

    panByPixels(gesture.lastX - event.clientX)
    gesture.lastX = event.clientX
    gesture.lastY = event.clientY
  }

  const handlePointerUpCapture = (event: PointerEvent<HTMLElement>) => {
    const gesture = pointerGestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return

    const wasDragged = gesture.moved
    const longPressTriggered = mobileLongPressTriggeredRef.current
    clearMobileLongPress()
    pointerGestureRef.current = null

    if (gesture.hasCapture) {
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId)
      } catch {
        // Pointer capture may already be released by the browser.
      }
    }

    if (wasDragged || longPressTriggered) {
      suppressClickRef.current = true
      window.setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
      return
    }

    lastTapRef.current = {
      time: window.performance.now(),
      x: event.clientX,
      y: event.clientY,
    }
  }

  const handlePointerCancelCapture = (event: PointerEvent<HTMLElement>) => {
    if (pointerGestureRef.current?.pointerId !== event.pointerId) return
    clearMobileLongPress()
    pointerGestureRef.current = null
    suppressClickRef.current = false
  }

  const handleClickCapture = (event: MouseEvent<HTMLElement>) => {
    if (!suppressClickRef.current) return
    event.preventDefault()
    event.stopPropagation()
    suppressClickRef.current = false
  }

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    if (isMobileViewport) return

    if (pointerGestureRef.current?.moved) {
      hideDashboardHover()
      return
    }

    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>('[data-hover-info]')
      : null

    if (!target?.dataset.hoverInfo) {
      if (dashboardHoverAnchorRef.current) hideDashboardHover()
      return
    }

    if (dashboardHoverAnchorRef.current === target) return
    showDashboardHover(target)
  }

  const activeGoal = activeGoalId ? elementById.get(activeGoalId) : null
  const activeTask = activeTaskId ? elementById.get(activeTaskId) : null
  const activeSubTask = activeSubTaskId ? elementById.get(activeSubTaskId) : null
  const canvasTargetElement =
    (selectedElementId ? elementById.get(selectedElementId) : null) ??
    activeSubTask ??
    activeTask ??
    activeGoal ??
    visibleElements[0] ??
    null
  const showDashboardDetailCard = Boolean(dashboardDetailElement)
  const detailDescription = dashboardDetailElement?.description?.trim() || t('NLSDashboardEmptyNoDescription')
  const detailDateFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }), [locale])
  const formatDetailDate = (value: TimelineElement['startDate']) => {
    if (!value) return t('NLSDashboardEmptyNoDate')
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? t('NLSDashboardEmptyNoDate') : detailDateFormatter.format(date)
  }
  const formatDetailType = (type: TimelineElement['type']) => {
    switch (type) {
      case 'SUBTASK':
        return t('NLSDashboardDetailTypeSubtask')
      case 'TASK':
        return t('NLSDashboardDetailTypeTask')
      case 'GOAL':
        return t('NLSDashboardDetailTypeGoal')
    }
  }
  const detailActionDisabled = !canRunDetailActions || isDetailActionRunning
  const detailPasteDisabled = detailActionDisabled || !canPasteIntoDetail
  const windowDurationMs = effectiveWindow ? Math.max(1, effectiveWindow.endMs - effectiveWindow.startMs) : 0
  const visibleDurationMs = visibleBounds ? Math.max(1, visibleBounds.endMs - visibleBounds.startMs) : 0
  const zoomPercent = windowDurationMs && visibleDurationMs
    ? Math.round((visibleDurationMs / windowDurationMs) * 100)
    : 100
  const windowDays = windowDurationMs ? Math.max(1, Math.round(windowDurationMs / (24 * 60 * 60 * 1000))) : 0
  const canvasGeometricZoomPercent = Math.round((canvasTransform.zoom || 1) * 100)
  const effectiveZoomPercent = viewControls.zoomMode === 'geometric' ? canvasGeometricZoomPercent : zoomPercent
  const currentZoomLabel =
    viewControls.zoomMode === 'geometric'
      ? `${canvasGeometricZoomPercent}%`
      : viewControls.zoom
        ? viewControls.zoom.label(zoomPercent, windowDays)
        : `${zoomPercent}%`
  const controlFocusElement = selectedElementId ? elementById.get(selectedElementId) ?? canvasTargetElement : canvasTargetElement
  const canFocusControl = Boolean(controlFocusElement?.startDate && controlFocusElement?.endDate)
  const pannableCanvasWorkspaceId = activeWorkspace?.id ?? activeWorkspaceId ?? 'unknown-workspace'
  const isCanvasPanEnabled = viewControls.canvasPan && Boolean(activeWorkspace?.id ?? activeWorkspaceId)
  const showSidePanControls = viewControls.datePan || viewControls.horizontalScroll
  const [isViewLegendOpen, setIsViewLegendOpen] = useState(false)
  const legendButtonRef = useRef<HTMLButtonElement | null>(null)
  const legendCloseTimerRef = useRef<number | null>(null)
  const openViewLegend = () => {
    if (legendCloseTimerRef.current !== null) {
      window.clearTimeout(legendCloseTimerRef.current)
      legendCloseTimerRef.current = null
    }
    setIsViewLegendOpen(true)
  }
  const closeViewLegendSoon = () => {
    if (legendCloseTimerRef.current !== null) window.clearTimeout(legendCloseTimerRef.current)
    legendCloseTimerRef.current = window.setTimeout(() => {
      setIsViewLegendOpen(false)
      legendCloseTimerRef.current = null
    }, 140)
  }
  useEffect(() => {
    setIsViewLegendOpen(false)
  }, [safeCurrentView])
  useEffect(() => () => {
    if (legendCloseTimerRef.current !== null) window.clearTimeout(legendCloseTimerRef.current)
  }, [])
  const legendApplicableViews: DashboardView[] = ['Constellation', 'Radar', 'Journey', 'Grid']
  const showViewLegendControl = legendApplicableViews.includes(safeCurrentView)
  const legendRangeFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }), [locale])
  const renderViewLegendContent = () => {
    if (!effectiveWindow) return null
    const { goals: legendGoals, tasks: legendTasks, subTasks: legendSubTasks } = splitMacroElements(boardElements)
    const legendToday = computeTodayContext(effectiveWindow.startMs, effectiveWindow.endMs, {
      timezone: viewProjection.workspaceTimezone,
    })
    const legendRange = `${legendRangeFormatter.format(new Date(effectiveWindow.startMs))} – ${legendRangeFormatter.format(new Date(effectiveWindow.endMs))}`
    const legendTodayLabel = legendToday.isTodayInWindow ? new Date(legendToday.todayMs).toLocaleDateString(locale) : null
    const highRiskCount = viewProjection.visualItems.filter((item) => item.risk.severity >= 4).length
    const isRadar = safeCurrentView === 'Radar'
    const isJourney = safeCurrentView === 'Journey'
    const isGrid = safeCurrentView === 'Grid'
    const heading = isRadar ? viewDockLabels.Radar.label : isJourney ? viewDockLabels.Journey.label : isGrid ? viewDockLabels.Grid.label : viewDockLabels.Constellation.label
    return (
      <>
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">{heading}</p>
        <p className="mt-1 text-sm font-black text-[var(--text-strong)]">
          {t('NLSDashboardLegendSummary', { goals: legendGoals.length, tasks: legendTasks.length, subtasks: legendSubTasks.length })}
        </p>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
          {isRadar ? t('NLSDashboardLegendWindow') : t('NLSDashboardLegendViewWindow')} · {legendRange}
          {legendTodayLabel && <span className="ml-1 text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">· {t('NLSDashboardLegendToday', { date: legendTodayLabel })}</span>}
        </p>
        {isRadar && (
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
            {t(highRiskCount === 1 ? 'NLSDashboardLegendHighRiskSignal' : 'NLSDashboardLegendHighRiskSignals', { count: highRiskCount })}
          </p>
        )}
        <ul className="mt-2 grid gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
          {isRadar && (
            <>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-[var(--chart-1)]" />
                <span>{t('NLSDashboardLegendRadarInnerOuter')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-[var(--chart-1)]/40" />
                <span>{t('NLSDashboardLegendAnglePosition')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-0.5 rounded-sm bg-[var(--warning)]" />
                <span>{t('NLSDashboardLegendRadarTodaySweep')}</span>
              </li>
            </>
          )}
          {isJourney && (
            <>
              <li className="flex items-center gap-2">
                <span className="inline-block h-0.5 w-4 rounded-sm bg-gradient-to-r from-[var(--chart-1)] via-[var(--accent)] to-[var(--chart-5)]" />
                <span>{t('NLSDashboardLegendJourneyPath')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-0.5 rounded-sm bg-[var(--warning)]" />
                <span>{t('NLSDashboardLegendAmberLineToday')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-[var(--chart-1)]" />
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--chart-5)]" />
                </span>
                <span>{t('NLSDashboardLegendDotsTasksSubtasks')}</span>
              </li>
            </>
          )}
          {isGrid && (
            <>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-[3px] bg-[var(--chart-1)]" />
                <span>{t('NLSDashboardLegendGridCell')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-[3px] ring-2 ring-[var(--success)]" />
                <span>{t('NLSDashboardLegendGridFrame')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-0.5 rounded-sm bg-[var(--info)]" />
                <span>{t('NLSDashboardLegendGridSkySpine')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex flex-col justify-center">
                  <span className="inline-block h-1 w-2.5 rounded-t-sm bg-[var(--chart-1)]" />
                  <span className="inline-block h-1 w-2.5 rounded-b-sm bg-[var(--chart-5)]" />
                </span>
                <span>{t('NLSDashboardLegendGridStripes')}</span>
              </li>
            </>
          )}
          {!isRadar && !isJourney && !isGrid && (
            <>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full border border-[var(--border)]" />
                <span>{t('NLSDashboardLegendConstellationRing')}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-0.5 rounded-sm bg-[var(--warning)]" />
                <span>{t('NLSDashboardLegendConstellationAmberTick')}</span>
              </li>
            </>
          )}
          <li className="flex items-center gap-2">
            <span className="inline-flex items-center gap-0.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--chart-1)]" />
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--chart-5)]" />
            </span>
            <span>{t('NLSDashboardLegendColorSize')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 rounded-[2px] bg-[var(--success)]" />
            <span>{t('NLSDashboardLegendDiamondChecklist')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--info)]" />
            <span>{t('NLSDashboardLegendSkyGlowSelected')}</span>
          </li>
        </ul>
      </>
    )
  }
  const viewSelectCrumb: CrumbSpec = {
    key: 'view',
    label: viewDockLabels[safeCurrentView].label,
    children: enabledViews.map<DropdownItem>((view) => ({
      key: view,
      label: viewDockLabels[view].label,
      onSelect: () => setDashboardView(view),
      isActive: view === safeCurrentView,
    })),
    ariaLabel: t('NLSDashboardAriaViewSelect'),
    tourId: 'dashboard-view-switcher',
  }

  return (
    <DashboardViewControlsSlotProvider slotEl={viewControlsSlotEl}>
    <section
      ref={viewportRef}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerMoveCapture={handlePointerMoveCapture}
      onPointerUpCapture={handlePointerUpCapture}
      onPointerCancelCapture={handlePointerCancelCapture}
      onClickCapture={handleClickCapture}
      onContextMenu={(event) => {
        if (event.currentTarget.contains(event.target as Node)) event.preventDefault()
      }}
      onPointerMove={handlePointerMove}
      onPointerLeave={() => {
        clearMobileLongPress()
        hideDashboardHover()
      }}
      data-dashboard-viewport="true"
      data-orbit-target="canvas"
      className={`dashboardViewport grid gap-3 rounded-lg border border-[var(--border)] bg-[color-mix(in_srgb,var(--surface)_92%,transparent)] p-3 shadow-sm${isStageExpanded ? ' dashboardViewport--expanded' : ''}`}
      style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
    >
      <header className="dashboardViewportHeader flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <Breadcrumb
          className="min-w-0 text-xs font-bold text-[var(--muted)]"
          ariaLabel={t('NLSDashboardAriaTimelineBreadcrumb')}
          crumbs={(() => {
            const trail: CrumbSpec[] = []
            trail.push({
              key: 'board',
              label: TIMELINE_BOARD_LABELS[activeBoard],
              onLabelClick: () => openDashboardLevel(),
              children: goalOptions.map<DropdownItem>((goal) => ({
                key: goal.id,
                label: goal.title,
                onSelect: () => openGoalLevel(goal),
                isActive: goal.id === activeGoal?.id,
              })),
              ariaLabel: t('NLSDashboardAriaBoard'),
            })
            if (activeGoal) {
              trail.push({
                key: 'goal',
                label: <span className="max-w-56 truncate">{activeGoal.title}</span>,
                onLabelClick: () => openGoalLevel(activeGoal),
                children: taskOptions.map<DropdownItem>((task) => ({
                  key: task.id,
                  label: task.title,
                  onSelect: () => openTaskLevel(activeGoal, task),
                  isActive: task.id === activeTask?.id,
                })),
                ariaLabel: t('NLSDashboardAriaGoal'),
              })
            }
            if (activeTask && activeGoal) {
              trail.push({
                key: 'task',
                label: <span className="max-w-56 truncate">{activeTask.title}</span>,
                onLabelClick: () => openTaskLevel(activeGoal, activeTask),
                children: subtaskOptions.map<DropdownItem>((subtask) => ({
                  key: subtask.id,
                  label: subtask.title,
                  onSelect: () => openSubTaskLevel(activeGoal, activeTask, subtask),
                  isActive: subtask.id === activeSubTask?.id,
                })),
                ariaLabel: t('NLSDashboardAriaTask'),
              })
            }
            if (activeSubTask && activeGoal && activeTask) {
              trail.push({
                key: 'subtask',
                label: <span className="max-w-56 truncate">{activeSubTask.title}</span>,
                onLabelClick: () => openSubTaskLevel(activeGoal, activeTask, activeSubTask),
                ariaLabel: t('NLSDashboardAriaSubtask'),
              })
            }
            return trail
          })()}
        />

        <div className="dashboardHeaderActions">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => void sendGhostEcho(null, false)}
            aria-label={t('NLSDashboardActionSendBoardToMobile')}
            title={t('NLSDashboardActionGhostEchoBoardToMobile')}
            disabled={!currentUser || !activeWorkspace}
            icon={<MobileScreenShareRoundedIcon fontSize="small" />}
          >
            {t('NLSDashboardActionGhostEcho')}
          </Button>
          {isWatchAvailable && onToggleWatch && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={onToggleWatch}
              pressed={isWatching}
              aria-label={isWatching ? t('NLSDashboardActionStopWatching') : t('NLSDashboardActionWatch')}
              title={isWatching ? t('NLSDashboardActionWatchingStopHint') : t('NLSDashboardActionWatch')}
              icon={isWatching ? <VisibilityRoundedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            >
              {isWatching ? t('NLSDashboardActionWatching') : t('NLSDashboardActionWatch')}
            </Button>
          )}
        </div>
      </header>

      <div className="dashboardViewWorkbench" data-no-dashboard-drag="true" aria-label={t('NLSDashboardAriaDashboardViewControls')}>
          <div className="dashboardViewWorkbench__views" aria-label={t('NLSDashboardAriaDashboardVisualView')}>
            <Breadcrumb
              className="dashboardViewSelect"
              ariaLabel={t('NLSDashboardAriaViewSelect')}
              crumbs={[viewSelectCrumb]}
            />
          </div>

          <div className="dashboardViewWorkbench__controls" role="group" aria-label={t('NLSDashboardAriaCurrentViewControls', { view: viewDockLabels[safeCurrentView].label })}>
            {/* View-specific controls slot — first child of the right-side
                controls cluster so it sits with (and right-aligns with) the
                rest of the workbench controls. Empty for most views (hidden
                via `:empty`); Radar fills it with Active/Overall +
                Past-due/Unscheduled chips. */}
            <div
              ref={setViewControlsSlotEl}
              className="dashboardViewWorkbench__slot"
              data-view-controls-slot=""
            />
            {showSidePanControls && (
              <div className="dashboardViewWorkbench__group" role="group" aria-label={t('NLSDashboardAriaPanControls')}>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tipGroup="dashboardToolbar"
                  onClick={() => shiftViewSideways(-1, 'large')}
                  label={dashboardControlLabels.scrollLeftLarge}
                  icon={<KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tipGroup="dashboardToolbar"
                  onClick={() => shiftViewSideways(-1, 'small')}
                  label={dashboardControlLabels.scrollLeft}
                  icon={<KeyboardArrowLeftRoundedIcon fontSize="small" />}
                />
              </div>
            )}
            {viewControls.zoom && (
              <div className="dashboardViewWorkbench__group" role="group" aria-label={t('NLSDashboardAriaZoomControls')}>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tipGroup="dashboardToolbar"
                  onClick={handleDashboardZoomOut}
                  label={dashboardControlLabels.zoomOut}
                  icon={<RemoveRoundedIcon fontSize="small" />}
                />
                <span className="dashboardViewReadout" aria-label={t('NLSDashboardAriaCurrentZoomPercent', { percent: effectiveZoomPercent })}>
                  {currentZoomLabel}
                  {viewControls.zoomMode === 'semantic' && (
                    <small>{t('NLSDashboardStatusDaySpan', { days: windowDays })}</small>
                  )}
                </span>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tipGroup="dashboardToolbar"
                  onClick={handleDashboardZoomIn}
                  label={dashboardControlLabels.zoomIn}
                  icon={<AddRoundedIcon fontSize="small" />}
                />
              </div>
            )}
            {(viewControls.fitTimeline || viewControls.focusElement) && (
              <div className="dashboardViewWorkbench__group" role="group" aria-label={t('NLSDashboardAriaFocusControls')}>
                {viewControls.fitTimeline && (
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    tipGroup="dashboardToolbar"
                    onClick={focusDashboardWindow}
                    label={dashboardControlLabels.fitTimeline}
                    icon={<FitScreenRoundedIcon fontSize="small" />}
                  />
                )}
                {viewControls.focusElement && (
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    tipGroup="dashboardToolbar"
                    onClick={() => focusElementWindow(controlFocusElement)}
                    label={dashboardControlLabels.focusSelectedItem}
                    disabled={!canFocusControl}
                    icon={<CenterFocusStrongRoundedIcon fontSize="small" />}
                  />
                )}
              </div>
            )}
            {showSidePanControls && (
              <div className="dashboardViewWorkbench__group" role="group" aria-label={t('NLSDashboardAriaPanForwardControls')}>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tipGroup="dashboardToolbar"
                  onClick={() => shiftViewSideways(1, 'small')}
                  label={dashboardControlLabels.scrollRight}
                  icon={<KeyboardArrowRightRoundedIcon fontSize="small" />}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tipGroup="dashboardToolbar"
                  onClick={() => shiftViewSideways(1, 'large')}
                  label={dashboardControlLabels.scrollRightLarge}
                  icon={<KeyboardDoubleArrowRightRoundedIcon fontSize="small" />}
                />
              </div>
            )}
            {showViewLegendControl && (
              <div
                className="dashboardViewWorkbench__group"
                role="group"
                aria-label={dashboardControlLabels.viewLegend}
                onMouseEnter={openViewLegend}
                onMouseLeave={closeViewLegendSoon}
              >
                <IconButton
                  ref={legendButtonRef}
                  variant="tertiary"
                  size="sm"
                  pressed={isViewLegendOpen}
                  onClick={() => (isViewLegendOpen ? closeViewLegendSoon() : openViewLegend())}
                  onFocus={openViewLegend}
                  onBlur={closeViewLegendSoon}
                  label={isViewLegendOpen ? dashboardControlLabels.hideViewLegend : dashboardControlLabels.showViewLegend}
                  aria-expanded={isViewLegendOpen}
                  title={dashboardControlLabels.viewLegend}
                  icon={<HelpOutlineRoundedIcon fontSize="small" />}
                />
              </div>
            )}
            {viewControls.expand && (
              <div className="dashboardViewWorkbench__group dashboardViewWorkbench__group--accent" role="group" aria-label={t('NLSDashboardAriaStageControls')}>
                <IconButton
                  variant="secondary"
                  size="sm"
                  pressed={isStageExpanded}
                  onClick={() => setIsStageExpanded((current) => !current)}
                  label={isStageExpanded ? dashboardControlLabels.exitExpandedView : dashboardControlLabels.expandDashboardView}
                  title={isStageExpanded ? dashboardControlLabels.exitExpandedView : dashboardControlLabels.expandDashboardView}
                  icon={isStageExpanded ? <CloseFullscreenRoundedIcon fontSize="small" /> : <OpenInFullRoundedIcon fontSize="small" />}
                />
              </div>
            )}
          </div>
        </div>

      <HoverBubble
        open={isViewLegendOpen}
        anchor={isViewLegendOpen ? legendButtonRef.current : null}
        content={isViewLegendOpen ? renderViewLegendContent() : null}
        variant="rich"
        placement="bottom-end"
        maxWidth={304}
        onRequestClose={() => setIsViewLegendOpen(false)}
      />

      {error && (
        <div className="rounded-md border border-[var(--danger)] bg-[var(--danger-soft)] px-3 py-2 text-sm font-bold text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">
          {error}
        </div>
      )}

      <LayoutGroup>
        <AnimatePresence mode="wait">
          <motion.div
            key={safeCurrentView}
            initial={{ opacity: 0, y: 12, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.99 }}
            transition={preferences.animationProfile === 'REDUCED_MOTION' ? viewTransition : { duration: 0.22, ease: 'easeOut' }}
            className={`dashboardViewStage${showDashboardDetailCard ? ' dashboardViewStage--withDetail' : ''}`}
          >
            {isLoading ? (
              <div className="grid min-h-80 place-items-center text-sm text-[var(--muted)] appInlineLoadingState"><Loader size={170} label={t('NLSDashboardStatusLoadingCanvas')} /></div>
            ) : isAccessRestricted && boardElements.length === 0 ? (
              <div className="grid min-h-80 place-items-center gap-1 text-center p-8">
                <p className="text-sm font-medium text-[var(--muted)]">{t('NLSScopeEmptyRestricted')}</p>
              </div>
            ) : (
              effectiveWindow && (
                <PannableCanvas
                  key={`${pannableCanvasWorkspaceId}:${safeCurrentView}`}
                  viewId={safeCurrentView}
                  workspaceId={pannableCanvasWorkspaceId}
                  enabled={isCanvasPanEnabled}
                  pan={viewControls.pan}
                  zoomAxis={viewControls.zoomAxis}
                  zoomMode={viewControls.zoomMode}
                  onSemanticZoom={zoomAt}
                  onControllerReady={handleCanvasControllerReady}
                  onTransformChange={handleCanvasTransformChange}
                >
                  <ActiveView
                    elements={activeViewElements}
                    visualItems={viewProjection.visualItems}
                    activityRecords={viewProjection.activityRecords}
                    activityByDate={viewProjection.activityByDate}
                    isActivityLoading={viewProjection.isActivityLoading}
                    viewStartMs={effectiveWindow.startMs}
                    viewEndMs={effectiveWindow.endMs}
                    selectedElementId={selectedElementId}
                    onElementClick={handleElementClick}
                    onChecklistClick={openChecklistDetail}
                    todayContext={computeTodayContext(effectiveWindow.startMs, effectiveWindow.endMs, {
                      timezone: viewProjection.workspaceTimezone,
                    })}
                    recurringOccurrencesById={buildRecurringOccurrenceMap(
                      viewProjection.visualItems,
                      effectiveWindow.startMs,
                      effectiveWindow.endMs,
                      { timezone: viewProjection.workspaceTimezone },
                    )}
                  />
                </PannableCanvas>
              )
            )}
          </motion.div>
        </AnimatePresence>
      </LayoutGroup>

      {showResourceReadinessPanel && (
        <ResourceReadinessPanel
          items={resourceReadiness.riskQueue}
          onOpenItem={openChecklistDetail}
        />
      )}

      {showDashboardDetailCard && dashboardDetailElement && (
        <motion.aside
          className="dashboardDetailCard"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          aria-label={t('NLSDashboardAriaSelectedItemDetails')}
          data-element-type={dashboardDetailElement.type}
        >
          <header className="dashboardDetailHeader">
            <div className="dashboardDetailHeader__title">
              <StatusChip
                size="sm"
                tone="neutral"
                className={`dashboardDetailTypeChip dashboardDetailTypeChip--${dashboardDetailElement.type.toLowerCase()}`}
              >
                {formatDetailType(dashboardDetailElement.type)}
              </StatusChip>
              <h3 title={dashboardDetailElement.title}>{dashboardDetailElement.title}</h3>
            </div>
            <div className="dashboardDetailHeader__dates" aria-label={t('NLSDashboardAriaItemDateRange')}>
              <span>
                <strong>{t('NLSDashboardDetailStart')}</strong>
                <em>{formatDetailDate(dashboardDetailElement.startDate)}</em>
              </span>
              <span className="dashboardDetailHeader__datesSep" aria-hidden>→</span>
              <span>
                <strong>{t('NLSDashboardDetailEnd')}</strong>
                <em>{formatDetailDate(dashboardDetailElement.endDate)}</em>
              </span>
            </div>
          </header>

          <div className="dashboardDetailBody">
            <div className="dashboardDetailDescription">
              <p>{detailDescription}</p>
            </div>

            <div className="dashboardDetailActions" aria-label={t('NLSDashboardAriaDetailActions')}>
              <div className="dashboardDetailActionsGroup" role="group" aria-label={t('NLSDashboardAriaShare')}>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label={t('NLSDashboardActionSendSelectedToMobile')}
                  title={t('NLSDashboardActionGhostEchoToMobile')}
                  disabled={!currentUser || !activeWorkspace}
                  onClick={() => void sendGhostEcho(dashboardDetailElement, false)}
                  icon={<MobileScreenShareRoundedIcon fontSize="inherit" />}
                />
              </div>
              <div className="dashboardDetailActionsGroup" role="group" aria-label={t('NLSDashboardAriaClipboard')}>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label={t('NLSDashboardActionCopyItem')}
                  title={t('NLSDashboardActionCopyItem')}
                  disabled={detailActionDisabled}
                  onClick={() => setDashboardClipboard({ op: 'copy', element: dashboardDetailElement })}
                  icon={<ContentCopyRoundedIcon fontSize="inherit" />}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label={t('NLSDashboardActionCutItem')}
                  title={t('NLSDashboardActionCutItem')}
                  disabled={detailActionDisabled || dashboardDetailElement.type === 'GOAL'}
                  onClick={() => setDashboardClipboard({ op: 'cut', element: dashboardDetailElement })}
                  icon={<ContentCutRoundedIcon fontSize="inherit" />}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label={t('NLSDashboardActionPasteIntoSelectedItem')}
                  title={t('NLSDashboardActionPasteItem')}
                  disabled={detailPasteDisabled}
                  onClick={() => {
                    if (!dashboardClipboard || !dashboardDetailElement) return
                    requestConfirm({
                      title: t('NLSDashboardDialogPasteTitle', { title: dashboardClipboard.element.title }),
                      message: (
                        <p>
                          {t('NLSDashboardDialogPastePrefix', {
                            action: dashboardClipboard.op === 'cut' ? t('NLSDashboardDialogPasteActionMove') : t('NLSDashboardDialogPasteActionCopy'),
                          })}{' '}
                          <strong>{dashboardClipboard.element.title}</strong>
                          {dashboardClipboard.element.type === 'TASK' ? t('NLSDashboardDialogPasteWithSubtasks') : ''}
                          {' '}{t('NLSDashboardDialogPasteInto')} <strong>{dashboardDetailElement.title}</strong> {t('NLSDashboardDialogPasteSuffix')}
                        </p>
                      ),
                      confirmLabel: dashboardClipboard.op === 'cut' ? t('NLSDashboardActionMoveHere') : t('NLSDashboardActionPasteCopy'),
                      run: () => pasteClipboardInto(dashboardDetailElement),
                    })
                  }}
                  icon={<ContentPasteRoundedIcon fontSize="inherit" />}
                />
              </div>
              {dashboardDetailElement.type === 'GOAL' && (
                <div className="dashboardDetailActionsGroup" role="group" aria-label={t('NLSDashboardAriaBoards')}>
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    label={t('NLSDashboardActionMoveGoalToVisionboard')}
                    title={t('NLSDashboardActionMoveToVisionboard')}
                    disabled={detailActionDisabled}
                    onClick={() => requestConfirm({
                      title: t('NLSDashboardDialogMoveVisionboardTitle', { title: dashboardDetailElement.title }),
                      message: (
                        <p>
                          {t('NLSDashboardDialogMoveVisionboardMessage')}
                        </p>
                      ),
                      confirmLabel: t('NLSDashboardActionMoveToVisionboard'),
                      run: () => moveGoalSubtreeToBoard(dashboardDetailElement, 'VISIONBOARD'),
                    })}
                    icon={<InterestsRoundedIcon fontSize="inherit" />}
                  />
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    label={t('NLSDashboardActionMoveGoalToArchive')}
                    title={t('NLSDashboardActionMoveToArchive')}
                    disabled={detailActionDisabled}
                    onClick={() => requestConfirm({
                      title: t('NLSDashboardDialogMoveArchiveTitle', { title: dashboardDetailElement.title }),
                      message: (
                        <p>
                          {t('NLSDashboardDialogMoveArchiveMessage')}
                        </p>
                      ),
                      confirmLabel: t('NLSDashboardActionMoveToArchive'),
                      run: () => moveGoalSubtreeToBoard(dashboardDetailElement, 'ARCHIVE'),
                    })}
                    icon={<ArchiveRoundedIcon fontSize="inherit" />}
                  />
                  <IconButton
                    variant="tertiary"
                    tone="danger"
                    size="sm"
                    label={t('NLSDashboardActionMoveGoalToBin')}
                    title={t('NLSDashboardActionMoveToBin')}
                    disabled={detailActionDisabled}
                    onClick={() => requestConfirm({
                      title: t('NLSDashboardDialogMoveBinTitle', { title: dashboardDetailElement.title }),
                      message: (
                        <p>
                          {t('NLSDashboardDialogMoveBinMessage')}
                        </p>
                      ),
                      confirmLabel: t('NLSDashboardActionMoveToBin'),
                      variant: 'danger',
                      run: () => moveGoalSubtreeToBoard(dashboardDetailElement, 'BIN'),
                    })}
                    icon={<DeleteRoundedIcon fontSize="inherit" />}
                  />
                </div>
              )}
            </div>
          </div>
        </motion.aside>
      )}

      <HoverBubble
        {...dashboardHover.bubbleProps}
        avoidZoneRects={collectDashboardAvoidZones}
      />

      <AppDialog
        open={Boolean(confirmIntent)}
        title={confirmIntent?.title ?? ''}
        message={confirmIntent?.message}
        onDismiss={() => {
          if (!isDetailActionRunning) setConfirmIntent(null)
        }}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'ghost',
            onClick: () => {
              if (!isDetailActionRunning) setConfirmIntent(null)
            },
          },
          {
            label: isDetailActionRunning ? t('NLSDashboardActionWorking') : confirmIntent?.confirmLabel ?? t('NLSDashboardActionConfirm'),
            variant: confirmIntent?.variant ?? 'primary',
            autoFocus: true,
            onClick: runConfirmedAction,
            disabled: isDetailActionRunning,
            loading: isDetailActionRunning,
          },
        ]}
      />

      {ghostEchoPromptOpen && (
        <AppDialog
          open={ghostEchoPromptOpen}
          size="sm"
          title={t('NLSDashboardActionGhostEcho')}
          onDismiss={() => {
            if (!isSendingGhostEcho) {
              setGhostEchoPromptOpen(false)
              setGhostEchoPayload(null)
            }
          }}
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'ghost',
              onClick: () => {
                if (!isSendingGhostEcho) {
                  setGhostEchoPromptOpen(false)
                  setGhostEchoPayload(null)
                }
              },
              disabled: isSendingGhostEcho,
            },
            {
              label: t('NLSCommonActionConfirm'),
              variant: 'primary',
              autoFocus: true,
              onClick: handleConfirmGhostEcho,
              disabled: !ghostEchoTargetUid.trim() || isSendingGhostEcho,
              loading: isSendingGhostEcho,
            },
          ]}
        >
          <div className="space-y-2">
            <label htmlFor="dashboardGhostEchoUid" className="text-sm font-medium text-[var(--text-strong)]">
              {t('NLSDashboardPromptGhostEchoUid')}
            </label>
            <TextInput
              id="dashboardGhostEchoUid"
              value={ghostEchoTargetUid}
              onChange={setGhostEchoTargetUid}
              autoFocus
              placeholder={currentUser?.uid ?? ''}
              disabled={isSendingGhostEcho}
            />
          </div>
        </AppDialog>
      )}
    </section>
    </DashboardViewControlsSlotProvider>
  )
}
